import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DataTable as PrimeDataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Sidebar } from 'primereact/sidebar';
import { Tooltip } from 'primereact/tooltip';
import { Toast } from 'primereact/toast';
import { Panel } from 'primereact/panel';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { AppCard, AppPage, CopyableText } from '../../components/general';
import { useAuwOrderHistoryDetail } from '../../hooks/useAuwTracing';
import { getFileContent, resendWebhookEvent } from '../../services/auwTracingService';
import {
  AuwOrderHistorySummaryViewModel,
  AuwServiceRequestHistoryViewModel,
} from '../../models/auwTracing';
import { WebhookEventViewModel } from '../../models/voxTracing';
import { ProcessedTraceLog, GroupedTraceLogs } from '../../models/tracing';
import { processTraceLogs, groupTraceLogs } from '../../utils/traceLogUtils';
import { toLocalDateTime, toLocalDateTimePrecise } from '../../utils/date-helper';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import TraceLogDetailPanel from '../../components/tracing/TraceLogDetailPanel';
import styles from '../../components/tracing/TraceLogList.module.css';

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Button
      icon={copied ? 'pi pi-check' : 'pi pi-copy'}
      label={copied ? 'Copied!' : 'Copy'}
      className="p-button-text p-button-sm"
      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', color: copied ? '#76b729' : '#8aabc8' }}
      onClick={handleCopy}
    />
  );
};

const JsonDisplay = ({ data }: { data: string }) => {
  let formatted: string;
  try {
    formatted = JSON.stringify(JSON.parse(data), null, 2);
  } catch {
    formatted = data;
  }
  return (
    <div className="rounded border-1 border-gray-200 overflow-hidden max-h-[48rem]" style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', top: '0.4rem', right: '0.5rem', zIndex: 10 }}>
        <CopyButton text={formatted} />
      </div>
      <SyntaxHighlighter
        language="json"
        style={nord}
        customStyle={{ margin: 0, padding: '1rem', fontSize: '0.875rem', maxHeight: '48rem' }}
        showLineNumbers
        wrapLongLines
      >
        {formatted}
      </SyntaxHighlighter>
    </div>
  );
};

const AuwOrderDetail = () => {
  const { auwOrderGuid } = useParams<{ auwOrderGuid: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const queryClient = useQueryClient();

  const { detailQuery } = useAuwOrderHistoryDetail(auwOrderGuid);

  const [selectedLog, setSelectedLog] = useState<ProcessedTraceLog | null>(null);
  const [sidePanelVisible, setSidePanelVisible] = useState(false);

  const [dataPanelVisible, setDataPanelVisible] = useState(false);
  const [dataPanelTitle, setDataPanelTitle] = useState('');
  const [dataPanelContent, setDataPanelContent] = useState('');

  const [selectedServiceRequest, setSelectedServiceRequest] = useState<AuwServiceRequestHistoryViewModel | null>(null);
  const [serviceRequestPanelVisible, setServiceRequestPanelVisible] = useState(false);

  const [selectedWebhook, setSelectedWebhook] = useState<WebhookEventViewModel | null>(null);
  const [webhookPanelVisible, setWebhookPanelVisible] = useState(false);
  const [resendingEventId, setResendingEventId] = useState<string | null>(null);

  const webhookPayloadQuery = useQuery<string>({
    queryKey: ['file-content', selectedWebhook?.requestPayloadFileId],
    queryFn: () => getFileContent(selectedWebhook!.requestPayloadFileId!),
    enabled: !!selectedWebhook?.requestPayloadFileId && webhookPanelVisible,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [hasInitialized, setHasInitialized] = useState(false);

  const processedLogs = detailQuery.data?.traceLogs ? processTraceLogs(detailQuery.data.traceLogs) : [];
  const groupedLogs = processedLogs.length > 0 ? groupTraceLogs(processedLogs) : [];

  useEffect(() => {
    if (!hasInitialized && groupedLogs && groupedLogs.length > 0) {
      const allGroupIds = new Set<string>();
      groupedLogs.forEach((group) => {
        if (group.traceGroup && !group.traceGroup.includes(':')) {
          allGroupIds.add(`tier2-${group.traceGroup}`);
          if (group.children && group.children.length > 0) {
            allGroupIds.add(`tier3-${group.traceGroup}`);
          }
        }
      });
      setExpandedGroups(allGroupIds);
      setHasInitialized(true);
    }
  }, [groupedLogs, hasInitialized]);

  const formatDateTime = (value: string | null) => {
    return value ? toLocalDateTime(value) : '—';
  };

  const formatTraceDateTime = (timestamp: string) => {
    return toLocalDateTimePrecise(timestamp) || timestamp;
  };

  const handleViewDetails = (log: ProcessedTraceLog) => {
    setSelectedLog(log);
    setSidePanelVisible(true);
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const getAllGroupIds = () => {
    const allGroupIds = new Set<string>();
    if (groupedLogs) {
      groupedLogs.forEach((group) => {
        if (group.traceGroup && !group.traceGroup.includes(':')) {
          allGroupIds.add(`tier2-${group.traceGroup}`);
          if (group.children && group.children.length > 0) {
            allGroupIds.add(`tier3-${group.traceGroup}`);
          }
        }
      });
    }
    return allGroupIds;
  };

  const handleExpandAll = () => setExpandedGroups(getAllGroupIds());
  const handleCollapseAll = () => setExpandedGroups(new Set());

  const statusSeverity: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
    Success: 'success',
    Completed: 'success',
    Pending: 'info',
    InProgress: 'info',
    AwaitingCallback: 'info',
    Failed: 'danger',
    Error: 'danger',
    Timeout: 'warning',
    Canceled: 'warning',
  };

  const serviceRequestPayloadQuery = useQuery<string>({
    queryKey: ['file-content', selectedServiceRequest?.requestData],
    queryFn: () => getFileContent(selectedServiceRequest!.requestData!),
    enabled: !!selectedServiceRequest?.requestData && serviceRequestPanelVisible && selectedServiceRequest.requestData.length === 36,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const renderLog = (log: ProcessedTraceLog, indentLevel: number = 0, isLastInGroup: boolean = false, isTier3InLastTier2: boolean = false) => {
    const hasDetails = !!(log.contextProperties || log.eventDataFileId);
    const descriptionIndent = indentLevel * 3;
    const bgColors = ['transparent', '#fafafa', '#f5f5f5', '#f0f0f0'];
    const bgColor = bgColors[indentLevel] || bgColors[bgColors.length - 1];

    return (
      <div
        key={`${log.id}`}
        className={`flex justify-content-between align-items-center py-2 hover:bg-blue-50 ${hasDetails ? 'cursor-pointer' : ''}`}
        style={{ backgroundColor: bgColor }}
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest('.trace-group-chip')) return;
          if (hasDetails) handleViewDetails(log);
        }}
      >
        <div className="flex gap-3 align-items-center flex-1">
          <div className={styles.chevronSpacer} />
          <span className={`text-sm text-gray-500 font-mono ${styles.timestamp}`}>
            {formatTraceDateTime(log.eventTimestamp)}
          </span>
          <span className={`text-base flex-1 ${styles.description}`} style={{ marginLeft: indentLevel > 0 ? `calc(${descriptionIndent}rem + 8px)` : `${descriptionIndent}rem` }}>
            {indentLevel === 1 && (
              <>
                <div className={styles.verticalLine} style={{
                  left: `${-descriptionIndent / 2}rem`,
                  top: '-0.5rem',
                  bottom: isLastInGroup ? '50%' : '-0.5rem',
                }} />
                <div className={styles.horizontalLine} style={{
                  left: `${-descriptionIndent / 2}rem`,
                  top: '50%',
                  width: `${descriptionIndent / 2}rem`,
                }} />
              </>
            )}
            {indentLevel === 2 && (
              <>
                <div className={styles.verticalLine} style={{
                  left: `${-descriptionIndent + (3 / 2)}rem`,
                  top: '-0.5rem',
                  bottom: (isLastInGroup && isTier3InLastTier2) ? '50%' : '-0.5rem',
                }} />
                <div className={styles.verticalLine} style={{
                  left: `${-descriptionIndent / 4}rem`,
                  top: '-0.5rem',
                  bottom: isLastInGroup ? '50%' : '-0.5rem',
                }} />
                <div className={styles.horizontalLine} style={{
                  left: `${-descriptionIndent / 4}rem`,
                  top: '50%',
                  width: `${descriptionIndent / 4}rem`,
                }} />
              </>
            )}
            {log.description || 'Event'}
          </span>
          {log.traceGroup && (
            <Tag
              value={log.traceGroup}
              className={`text-xs font-mono trace-group-chip ${styles.traceGroupChip}`}
              data-pr-tooltip={log.traceGroupDescription || log.description || log.traceGroup}
              data-pr-position="top"
            />
          )}
        </div>
      </div>
    );
  };

  const renderFlatLogs = (groups: GroupedTraceLogs[]) => {
    const tier1Logs: ProcessedTraceLog[] = [];
    const tier2ByGroup: Map<string, ProcessedTraceLog[]> = new Map();
    const tier3ByParent: Map<string, ProcessedTraceLog[]> = new Map();

    groups.forEach((group) => {
      group.logs.forEach((log) => {
        if (!log.traceGroup) {
          tier1Logs.push(log);
        } else if (!log.traceGroup.includes(':')) {
          if (!tier2ByGroup.has(log.traceGroup)) {
            tier2ByGroup.set(log.traceGroup, []);
          }
          tier2ByGroup.get(log.traceGroup)!.push(log);
        }
      });

      if (group.traceGroup && !group.traceGroup.includes(':') && group.children && group.children.length > 0) {
        const parentGroup = group.traceGroup;
        if (!tier3ByParent.has(parentGroup)) {
          tier3ByParent.set(parentGroup, []);
        }
        group.children.forEach((childGroup) => {
          tier3ByParent.get(parentGroup)!.push(...childGroup.logs);
        });
      }
    });

    tier1Logs.sort((a, b) => new Date(a.eventTimestamp).getTime() - new Date(b.eventTimestamp).getTime());
    tier2ByGroup.forEach((logs) => logs.sort((a, b) => new Date(a.eventTimestamp).getTime() - new Date(b.eventTimestamp).getTime()));
    tier3ByParent.forEach((logs) => logs.sort((a, b) => new Date(a.eventTimestamp).getTime() - new Date(b.eventTimestamp).getTime()));

    const timeline: Array<{ type: 'tier1' | 'tier2Group'; log: ProcessedTraceLog; groupLogs?: ProcessedTraceLog[] }> = [];

    tier1Logs.forEach((log) => {
      timeline.push({ type: 'tier1', log });
    });

    tier2ByGroup.forEach((logs) => {
      timeline.push({ type: 'tier2Group', log: logs[0], groupLogs: logs });
    });

    timeline.sort((a, b) => new Date(a.log.eventTimestamp).getTime() - new Date(b.log.eventTimestamp).getTime());

    const allElements: JSX.Element[] = [];

    timeline.forEach((item) => {
      if (item.type === 'tier1') {
        allElements.push(renderLog(item.log));
      } else if (item.type === 'tier2Group' && item.groupLogs) {
        const groupId = item.log.traceGroup!;
        const tier2Logs = item.groupLogs;
        const firstTier2 = tier2Logs[0];
        const tier2GroupId = `tier2-${groupId}`;
        const isTier2Expanded = expandedGroups.has(tier2GroupId);
        const tier3Logs = tier3ByParent.get(groupId) || [];
        const tier3GroupId = `tier3-${groupId}`;
        const isTier3Expanded = expandedGroups.has(tier3GroupId);

        allElements.push(
          <div
            key={`toggle-${tier2GroupId}`}
            className={`flex align-items-center py-2 hover:bg-blue-50 ${styles.toggleRow}`}
          >
            <div className="flex gap-3 align-items-center flex-1">
              <div
                className={`chevron-area flex align-items-center justify-content-center cursor-pointer ${styles.chevronArea}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGroup(tier2GroupId);
                }}
              >
                <i
                  className={`pi ${isTier2Expanded ? 'pi-chevron-down' : 'pi-chevron-right'} text-gray-400`}
                  style={{ fontSize: '0.75rem' }}
                />
              </div>
              <span className={`text-sm text-gray-500 font-mono ${styles.timestamp}`}>
                {formatTraceDateTime(firstTier2.eventTimestamp)}
              </span>
              <span className={`text-base flex-1 font-semibold ${styles.description}`}>
                {firstTier2.traceGroupDescription || firstTier2.description || groupId}
              </span>
              <Tag
                value={groupId}
                className={`text-xs font-mono trace-group-chip ${styles.traceGroupChip}`}
                data-pr-tooltip={firstTier2.traceGroupDescription || firstTier2.description || groupId}
                data-pr-position="top"
              />
            </div>
          </div>
        );

        if (isTier2Expanded) {
          tier2Logs.forEach((log, idx) => {
            allElements.push(renderLog(log, 1, idx === tier2Logs.length - 1 && tier3Logs.length === 0));
          });

          if (tier3Logs.length > 0) {
            allElements.push(
              <div
                key={`toggle-${tier3GroupId}`}
                className={`flex align-items-center py-1 hover:bg-blue-50 ${styles.toggleRow}`}
                style={{ marginLeft: '3rem' }}
              >
                <div className="flex gap-3 align-items-center flex-1">
                  <div
                    className={`chevron-area flex align-items-center justify-content-center cursor-pointer ${styles.chevronArea}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroup(tier3GroupId);
                    }}
                  >
                    <i
                      className={`pi ${isTier3Expanded ? 'pi-chevron-down' : 'pi-chevron-right'} text-gray-400`}
                      style={{ fontSize: '0.65rem' }}
                    />
                  </div>
                  <span className="text-sm text-gray-500">
                    Vendor Requests ({tier3Logs.length})
                  </span>
                </div>
              </div>
            );

            if (isTier3Expanded) {
              tier3Logs.forEach((log, idx) => {
                allElements.push(renderLog(log, 2, idx === tier3Logs.length - 1, true));
              });
            }
          }
        }
      }
    });

    return allElements;
  };

  if (detailQuery.isLoading) {
    return (
      <AppPage title="AUW Order Detail" breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'AUW', to: '/products/auw' }, { label: 'Transaction Logs', to: '/auw-tracing' }, { label: 'Order Detail' }]}>
        <div className="flex justify-content-center p-5">
          <ProgressSpinner />
        </div>
      </AppPage>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <AppPage title="AUW Order Detail" breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'AUW', to: '/products/auw' }, { label: 'Transaction Logs', to: '/auw-tracing' }, { label: 'Order Detail' }]}>
        <AppCard>
          <div className="text-red-500">
            {detailQuery.isError
              ? `Error: ${detailQuery.error instanceof Error ? detailQuery.error.message : 'Failed to load'}`
              : 'AUW order not found'}
          </div>
          <Button label="Back" icon="pi pi-arrow-left" className="mt-3" onClick={() => navigate(-1)} />
        </AppCard>
      </AppPage>
    );
  }

  const detail = detailQuery.data;

  return (
    <AppPage title="AUW Order Detail" breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'AUW', to: '/products/auw' }, { label: 'Transaction Logs', to: '/auw-tracing' }, { label: 'Order Detail' }]}>
      <Toast ref={toast} />
      <div className="flex justify-content-between align-items-center mb-3">
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          className="p-button-text"
          onClick={() => navigate(-1)}
        />
        <div className="flex gap-2">
          {detail.waterfallExecutionRecordGuid && (
            <Button
              icon="pi pi-sitemap"
              label="View Waterfall"
              className="p-button-outlined p-button-sm"
              onClick={() => navigate(`/vox-tracing/waterfall/${detail.waterfallExecutionRecordGuid}`)}
            />
          )}
          <Button
            icon="pi pi-refresh"
            label="Refresh"
            className="p-button-outlined p-button-sm"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['auw-order-history-detail'] })}
            loading={detailQuery.isRefetching}
          />
        </div>
      </div>

      <AppCard>
        <div className="flex flex-column gap-4">
          <div className="flex flex-row justify-content-between align-items-center">
            <h2 className="text-xl font-bold m-0">
              {detail.integrationVendor} — {detail.transactionName}
            </h2>
            <Tag
              value={detail.status?.toString() || 'Unknown'}
              severity={statusSeverity[detail.status?.toString()] || 'info'}
              className="text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-500 text-sm">Loan Number</span>
              <CopyableText value={detail.loanNumber} label="Loan Number" toast={toast} />
            </div>
            <div>
              <span className="text-500 text-sm">Loan Identifier</span>
              <CopyableText value={detail.loanIdentifier} label="Loan Identifier" toast={toast} />
            </div>
            <div>
              <span className="text-500 text-sm">Lead Number</span>
              <CopyableText value={detail.leadNumber} label="Lead Number" toast={toast} />
            </div>
            <div>
              <span className="text-500 text-sm">Vendor</span>
              <div className="font-semibold">{detail.integrationVendor}</div>
            </div>
            <div>
              <span className="text-500 text-sm">AUW Order GUID</span>
              <CopyableText value={detail.auwOrderHistoryGuid} label="AUW Order GUID" toast={toast} />
            </div>
            <div>
              <span className="text-500 text-sm">Status Message</span>
              <div className="font-semibold">{detail.statusMessage || '—'}</div>
            </div>
            <div>
              <span className="text-500 text-sm">Created</span>
              <div className="font-semibold">{formatDateTime(detail.createdDate)}</div>
            </div>
            <div>
              <span className="text-500 text-sm">Modified</span>
              <div className="font-semibold">{formatDateTime(detail.modifiedDate)}</div>
            </div>
            <div>
              <span className="text-500 text-sm">Order Complete Date</span>
              <div className="font-semibold">{formatDateTime(detail.orderCompleteDate)}</div>
            </div>
          </div>
        </div>
      </AppCard>

      {(detail.requestData || detail.responseData) && (
        <AppCard className="mt-3">
          <h3 className="text-lg font-semibold mb-3">Provider Request / Response</h3>
          <div className="flex gap-2">
            {detail.requestData && (
              <Button
                label="View Request Data"
                icon="pi pi-file"
                className="p-button-outlined"
                onClick={() => {
                  setDataPanelTitle('Request Data');
                  setDataPanelContent(detail.requestData!);
                  setDataPanelVisible(true);
                }}
              />
            )}
            {detail.responseData && (
              <Button
                label="View Response Data"
                icon="pi pi-file"
                className="p-button-outlined"
                onClick={() => {
                  setDataPanelTitle('Response Data');
                  setDataPanelContent(detail.responseData!);
                  setDataPanelVisible(true);
                }}
              />
            )}
          </div>
        </AppCard>
      )}

      {detail.serviceRequests && detail.serviceRequests.length > 0 && (
        <AppCard className="mt-3">
          <h3 className="text-lg font-semibold mb-3">Service Requests</h3>
          <PrimeDataTable
            value={detail.serviceRequests}
            selectionMode="single"
            onSelectionChange={(e: any) => {
              setSelectedServiceRequest(e.value as AuwServiceRequestHistoryViewModel);
              setServiceRequestPanelVisible(true);
            }}
            emptyMessage="No service requests"
            className="cursor-pointer"
            paginator
            rows={10}
          >
            <Column
              field="createdDate"
              header="DATE"
              body={(row: AuwServiceRequestHistoryViewModel) => formatDateTime(row.createdDate)}
              sortable
            />
            <Column field="transactionName" header="TRANSACTION" sortable />
            <Column
              field="status"
              header="STATUS"
              body={(row: AuwServiceRequestHistoryViewModel) => (
                <Tag
                  value={row.status?.toString() || 'Unknown'}
                  severity={statusSeverity[row.status?.toString()] || 'info'}
                />
              )}
              sortable
            />
            <Column field="providerServiceRequestId" header="PROVIDER REQUEST ID" />
            <Column
              field="serviceRequestCompleteDate"
              header="COMPLETED"
              body={(row: AuwServiceRequestHistoryViewModel) => formatDateTime(row.serviceRequestCompleteDate)}
              sortable
            />
          </PrimeDataTable>
        </AppCard>
      )}

      <AppCard className="mt-3">
        <div className="flex justify-content-between align-items-center mb-3">
          <h3 className="text-lg font-semibold m-0">Trace Logs</h3>
          {groupedLogs.length > 0 && (
            <div className="flex gap-2">
              <Button label="Expand All" icon="pi pi-plus" onClick={handleExpandAll} size="small" outlined />
              <Button label="Collapse All" icon="pi pi-minus" onClick={handleCollapseAll} size="small" outlined />
            </div>
          )}
        </div>
        <Tooltip target=".trace-group-chip" />
        {groupedLogs.length > 0 ? (
          <div className="trace-log-list">
            {renderFlatLogs(groupedLogs)}
          </div>
        ) : (
          <div className="text-500">No trace logs found for this order.</div>
        )}
      </AppCard>

      {detail.webhookEvents && detail.webhookEvents.length > 0 && (
        <AppCard className="mt-3">
          <h3 className="text-lg font-semibold mb-3">Webhook Events</h3>
          <PrimeDataTable
            value={detail.webhookEvents}
            selectionMode="single"
            onSelectionChange={(e: any) => {
              setSelectedWebhook(e.value as WebhookEventViewModel);
              setWebhookPanelVisible(true);
            }}
            emptyMessage="No webhook events"
            className="cursor-pointer"
            paginator
            rows={10}
          >
            <Column
              field="eventTimestamp"
              header="TIMESTAMP"
              body={(row: WebhookEventViewModel) => formatDateTime(row.eventTimestamp)}
              sortable
            />
            <Column field="eventType" header="EVENT TYPE" sortable />
            <Column
              field="httpStatus"
              header="HTTP STATUS"
              body={(row: WebhookEventViewModel) => (
                <Tag
                  value={String(row.httpStatus)}
                  severity={row.httpStatus >= 200 && row.httpStatus < 300 ? 'success' : 'danger'}
                />
              )}
              sortable
            />
            <Column field="vendor" header="VENDOR" sortable />
            <Column field="attempt" header="ATTEMPT" />
            <Column
              header=""
              body={(row: WebhookEventViewModel) => (
                <Button
                  icon="pi pi-replay"
                  label="Resend"
                  className="p-button-outlined p-button-sm p-button-warning"
                  loading={resendingEventId === row.webhookEventId}
                  onClick={async (e) => {
                    e.stopPropagation();
                    setResendingEventId(row.webhookEventId);
                    try {
                      await resendWebhookEvent(row.webhookEventId);
                      toast.current?.show({
                        severity: 'success',
                        summary: 'Webhook Resent',
                        detail: 'The webhook event has been resent successfully.',
                        life: 3000,
                      });
                      queryClient.invalidateQueries({ queryKey: ['auw-order-history-detail'] });
                    } catch (err) {
                      toast.current?.show({
                        severity: 'error',
                        summary: 'Resend Failed',
                        detail: err instanceof Error ? err.message : 'Failed to resend webhook event.',
                        life: 5000,
                      });
                    } finally {
                      setResendingEventId(null);
                    }
                  }}
                />
              )}
              style={{ width: '10rem' }}
            />
          </PrimeDataTable>
        </AppCard>
      )}

      {detail.relatedOrders && detail.relatedOrders.length > 0 && (
        <AppCard className="mt-3">
          <h3 className="text-lg font-semibold mb-3">Related Orders</h3>
          <PrimeDataTable
            value={detail.relatedOrders}
            selectionMode="single"
            onSelectionChange={(e: any) => {
              const order = e.value as AuwOrderHistorySummaryViewModel;
              if (order?.auwOrderHistoryGuid) {
                navigate(`/auw-tracing/order/${order.auwOrderHistoryGuid}`);
              }
            }}
            emptyMessage="No related orders"
            className="cursor-pointer"
          >
            <Column field="transactionName" header="TRANSACTION" sortable />
            <Column field="integrationVendor" header="VENDOR" sortable />
            <Column
              field="status"
              header="STATUS"
              body={(row: AuwOrderHistorySummaryViewModel) => (
                <Tag
                  value={row.status?.toString() || 'Unknown'}
                  severity={statusSeverity[row.status?.toString()] || 'info'}
                />
              )}
              sortable
            />
            <Column
              header="CREATED"
              body={(row: AuwOrderHistorySummaryViewModel) => formatDateTime(row.createdDate)}
              sortable
            />
            <Column
              header="MODIFIED"
              body={(row: AuwOrderHistorySummaryViewModel) => formatDateTime(row.modifiedDate)}
              sortable
            />
          </PrimeDataTable>
        </AppCard>
      )}

      <Sidebar
        visible={dataPanelVisible}
        position="right"
        onHide={() => setDataPanelVisible(false)}
        style={{ width: '50vw' }}
        header={dataPanelTitle}
      >
        {dataPanelContent && <JsonDisplay data={dataPanelContent} />}
      </Sidebar>

      <Sidebar
        visible={webhookPanelVisible}
        position="right"
        onHide={() => setWebhookPanelVisible(false)}
        className={`trace-log-sidebar ${styles.sidebarPanel}`}
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-bolt text-xl" />
            <span className="font-semibold">Webhook Event Detail</span>
          </div>
        }
      >
        {selectedWebhook && (
          <div className="flex flex-column gap-4">
            <Panel header="Event Information" className="w-full">
              <div className="flex flex-column gap-3">
                <div>
                  <div className="font-semibold text-sm text-gray-600 mb-1">Timestamp</div>
                  <div className="text-base font-mono">{formatDateTime(selectedWebhook.eventTimestamp)}</div>
                </div>
                <div>
                  <div className="font-semibold text-sm text-gray-600 mb-1">Event Type</div>
                  <div className="text-base">{selectedWebhook.eventType}</div>
                </div>
                <div>
                  <div className="font-semibold text-sm text-gray-600 mb-1">HTTP Status</div>
                  <div className="text-base">
                    <Tag
                      value={String(selectedWebhook.httpStatus)}
                      severity={selectedWebhook.httpStatus >= 200 && selectedWebhook.httpStatus < 300 ? 'success' : 'danger'}
                    />
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-sm text-gray-600 mb-1">Vendor</div>
                  <div className="text-base">{selectedWebhook.vendor || '—'}</div>
                </div>
                <div>
                  <div className="font-semibold text-sm text-gray-600 mb-1">Attempt</div>
                  <div className="text-base">{selectedWebhook.attempt}</div>
                </div>
                <div>
                  <div className="font-semibold text-sm text-gray-600 mb-1">URL</div>
                  <div className="text-sm font-mono bg-gray-100 p-2 rounded overflow-auto" style={{ wordBreak: 'break-all' }}>{selectedWebhook.url}</div>
                </div>
              </div>
            </Panel>

            {selectedWebhook.requestPayloadFileId && (
              <Panel
                header={
                  <div className="flex align-items-center gap-2">
                    <span>Request Payload</span>
                    <Tag value="JSON" severity="info" />
                  </div>
                }
                toggleable
                collapsed={false}
                className="w-full"
              >
                {webhookPayloadQuery.isLoading && (
                  <div className="flex align-items-center justify-content-center gap-2 py-4">
                    <ProgressSpinner style={{ width: '24px', height: '24px' }} strokeWidth="4" />
                    <span className="text-sm text-gray-500">Loading payload...</span>
                  </div>
                )}
                {webhookPayloadQuery.isError && (
                  <div className="text-red-500 text-sm p-3">
                    <i className="pi pi-exclamation-triangle mr-2" />
                    Failed to load request payload
                  </div>
                )}
                {webhookPayloadQuery.data && (
                  <JsonDisplay data={webhookPayloadQuery.data} />
                )}
              </Panel>
            )}

            {selectedWebhook.response && (
              <Panel
                header={
                  <div className="flex align-items-center gap-2">
                    <span>Response</span>
                    <Tag value="JSON" severity="success" />
                  </div>
                }
                toggleable
                collapsed={false}
                className="w-full"
              >
                <JsonDisplay data={selectedWebhook.response} />
              </Panel>
            )}
          </div>
        )}
      </Sidebar>

      <Sidebar
        visible={serviceRequestPanelVisible}
        position="right"
        onHide={() => {
          setServiceRequestPanelVisible(false);
          setSelectedServiceRequest(null);
        }}
        style={{ width: '50vw' }}
        header="Service Request Detail"
      >
        {selectedServiceRequest && (
          <div className="flex flex-column gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-500 text-sm">Transaction</span>
                <div className="font-semibold">{selectedServiceRequest.transactionName}</div>
              </div>
              <div>
                <span className="text-500 text-sm">Status</span>
                <div>
                  <Tag
                    value={selectedServiceRequest.status?.toString() || 'Unknown'}
                    severity={statusSeverity[selectedServiceRequest.status?.toString()] || 'info'}
                  />
                </div>
              </div>
              <div>
                <span className="text-500 text-sm">Provider Request ID</span>
                <div className="font-semibold">{selectedServiceRequest.providerServiceRequestId || '—'}</div>
              </div>
              <div>
                <span className="text-500 text-sm">Created</span>
                <div className="font-semibold">{formatDateTime(selectedServiceRequest.createdDate)}</div>
              </div>
              <div>
                <span className="text-500 text-sm">Completed</span>
                <div className="font-semibold">{formatDateTime(selectedServiceRequest.serviceRequestCompleteDate)}</div>
              </div>
            </div>
            {selectedServiceRequest.requestData && (
              <Panel header="Request Data" toggleable collapsed>
                {selectedServiceRequest.requestData.length === 36 ? (
                  <>
                    {serviceRequestPayloadQuery.isLoading && (
                      <div className="flex align-items-center justify-content-center gap-2 py-4">
                        <ProgressSpinner style={{ width: '24px', height: '24px' }} strokeWidth="4" />
                        <span className="text-sm text-gray-500">Loading payload...</span>
                      </div>
                    )}
                    {serviceRequestPayloadQuery.isError && (
                      <div className="text-red-500 text-sm p-3">
                        <i className="pi pi-exclamation-triangle mr-2" />
                        Failed to load request payload
                      </div>
                    )}
                    {serviceRequestPayloadQuery.data && (
                      <JsonDisplay data={serviceRequestPayloadQuery.data} />
                    )}
                  </>
                ) : (
                  <JsonDisplay data={selectedServiceRequest.requestData} />
                )}
              </Panel>
            )}
            {selectedServiceRequest.responseData && (
              <Panel header="Response Data" toggleable collapsed>
                <JsonDisplay data={selectedServiceRequest.responseData} />
              </Panel>
            )}
          </div>
        )}
      </Sidebar>

      <Sidebar
        visible={sidePanelVisible}
        position="right"
        onHide={() => setSidePanelVisible(false)}
        style={{ width: '50vw' }}
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-list text-xl" />
            <span className="font-semibold">Trace Log Details</span>
          </div>
        }
      >
        {selectedLog && <TraceLogDetailPanel log={selectedLog} />}
      </Sidebar>
    </AppPage>
  );
};

export default AuwOrderDetail;
